/**
 * Le moteur. Une seule porte d'entrée : simuler(etat, maintenant, contenu).
 *
 * GDD §14.1 : l'état ne s'incrémente jamais image par image. On calcule
 * toujours depuis `lastTick`. Un delta de 8 heures se traite exactement comme
 * un delta de 16 ms ; activer la progression hors-ligne ne sera qu'un
 * changement de PLAFOND_DELTA_MS.
 *
 * GDD §14.2 : module pur. Pas de DOM, pas de window, pas de réseau, pas de
 * Math.random. Le contenu de jeu est injecté, jamais chargé d'ici.
 */
import type { Etat, Personnage } from "./etat.ts"
import type { Contenu, Monstre, Item } from "./contenu.ts"
import { famille, monstre, item, recolte, donjon } from "./contenu.ts"
import { entrer, pasDonjon, possedeCles, type Descente } from "./donjon.ts"
import { effetDe, coutSlot, SLOTS_MAX } from "./classes.ts"
import {
  TAUX_ARCHIMONSTRE, TAUX_RECHERCHE, DUREE_ARCHIMONSTRE_MS,
  tirer, expiree, type Apparition,
} from "./collection.ts"
import {
  BONUS_ALMANAX, offrandeDuJour, jourDe, almanaxActif, finAlmanax,
} from "./almanax.ts"
import { effetsDofus, dofusMerites } from "./dofus.ts"
import { nouveaux, panopliesActives } from "./succes.ts"
import {
  type Recette, type Porte, type Slot,
  craftable, consommer, jeter, perfection, slotDe, broyage, portable,
  estConsommable, soinBrut, DELAI_REPAS_MS,
} from "./objets.ts"
import { alea } from "./alea.ts"
import { CHASSEUR, gibiers, xpParGibier, bonusChasse } from "./chasse.ts"
import { tempsPourUn } from "./rarete.ts"
import { forger, maudire, lignes, auMaximum, type Issue } from "./forge.ts"
import {
  statsPerso, palierDe, tempsParKill, dpsMonstre, degatsSubisPs,
  xpRequise, xpGagnee, POINTS_PAR_NIVEAU,
} from "./combat.ts"
import {
  accessible, tempsRecolte, xpParRecolte, xpRequiseMetier,
  niveauMetier, prixVente, NIVEAU_METIER_MAX,
} from "./recolte.ts"

/**
 * Delta maximal pris en compte en une fois.
 *
 * En v1 le jeu est « onglet ouvert » : au-delà de ce plafond, on considère que
 * l'onglet a été suspendu ou la machine endormie, et le temps ne compte pas.
 * Le jour où l'on active la progression hors-ligne (GDD §17), il suffit de
 * monter cette valeur, rien d'autre à changer.
 */
export const PLAFOND_DELTA_MS = 60_000

/** Granularité d'un pas. Découper garde le résultat identique quel que soit le
 *  rythme d'appel (60 fps ou un seul appel par minute). */
const PAS_MS = 250

/** Garde-fou : un temps par kill minuscule ne doit pas boucler sans fin. */
const KILLS_MAX_PAR_PAS = 200

export function simuler(etat: Etat, maintenant: number, contenu: Contenu): Etat {
  let delta = maintenant - etat.lastTick

  if (delta <= 0) {
    // horloge qui recule (changement d'heure, resynchronisation) : on recale
    // sans rien accorder plutôt que d'accumuler du négatif.
    return { ...etat, lastTick: maintenant }
  }

  if (delta > PLAFOND_DELTA_MS) delta = PLAFOND_DELTA_MS

  const rng = alea(etat.seed)
  let e: Etat = { ...etat }

  let restant = delta
  while (restant > 0) {
    const pas = Math.min(PAS_MS, restant)
    e = pasDeSimulation(e, pas, rng, contenu)
    restant -= pas
  }

  // Les Dofus s'obtiennent dès que leur condition est remplie : c'est une
  // récompense de progression, pas une quête à valider (GDD §11).
  const merites = dofusMerites(e, contenu)
  if (merites.length > 0) e = { ...e, dofus: [...e.dofus, ...merites] }

  // Les panoplies complétées **s'accumulent** : on ne porte qu'un équipement à
  // la fois, et compter celles qui sont actives à l'instant rendait « trois
  // panoplies » impossible — en changer faisait retomber le compte à une.
  const vues = panopliesActives(e, contenu)
  if (vues.length !== (e.panopliesVues ?? []).length) e = { ...e, panopliesVues: vues }

  // Un succès obtenu ne se reperd jamais. Il se mesurait sur l'état seul, ce
  // qui allait tant que la mesure ne pouvait que monter — mais la fortune se
  // dépense : atteindre cent mille kamas décrochait le succès, les dépenser le
  // reprenait. On n'évalue que ceux qui ne sont pas encore acquis.
  const frais = nouveaux(e, contenu)
  if (frais.length > 0) {
    const succes = { ...(e.succes ?? {}) }
    for (const cle of frais) succes[cle] = maintenant
    e = { ...e, succes }
  }

  return { ...e, lastTick: maintenant, seed: rng.graine }
}

function pasDeSimulation(etat: Etat, deltaMs: number, rng: () => number, c: Contenu): Etat {
  if (etat.personnages.length === 0) return etat

  const dt = deltaMs / 1000
  const bonusAlma = almanaxActif(etat.almanaxJusqua, etat.lastTick) ? BONUS_ALMANAX : 0
  const compte = effetsDofus(etat)
  const butin: Record<number, number> = {}
  // Les repas se prennent dans cette copie, qui devient l'inventaire du pas.
  const inventaire = { ...etat.inventaire }
  const mange: Record<number, number> = {}
  let butinRecolte = false
  // Collections (GDD §10) : accumulées pendant le pas, appliquées à la fin.
  let apparition = etat.apparition
  let archiRates = etat.archiRates
  const recherches = [...etat.recherches]
  let dernierRecherche = etat.dernierRecherche

  // Un archimonstre laissé trop longtemps est perdu : c'est le prix de l'absence.
  if (expiree(apparition, etat.lastTick + deltaMs)) {
    apparition = null
    archiRates++
  }

  // Chaque personnage remplit son propre butin, ensuite fusionné dans `butin`.
  // Sans cette séparation, le journal, qui lit une différence d'état, ne peut
  // pas dire *qui* a ramassé quoi, l'inventaire étant commun au compte.
  const parPerso = new Map<string, Record<number, number>>()
  // Les victoires par famille, pour les succès : « abattre mille Bouftous ».
  const killsFamille: Record<number, number> = { ...(etat.killsFamille ?? {}) }
  // Les descentes qui s'achèvent pendant ce pas : leur compte rendu remplace
  // celui d'avant, et le boss abattu rejoint la collection.
  const fins: Descente[] = []

  const personnages = etat.personnages.map((brut) => {
    const mien: Record<number, number> = {}
    parPerso.set(brut.id, mien)
    // Le délai entre deux repas s'écoule toujours, même à l'arrêt : sinon un
    // personnage tombé garderait son verrou indéfiniment.
    const attente = Math.max(0, (brut.repasDans ?? 0) - deltaMs)
    const p = attente === (brut.repasDans ?? 0) ? brut : { ...brut, repasDans: attente }
    if (!p.activite || p.arrete) return p
    if (p.activite.type === "recolte") {
      const maj = pasRecolte(p, p.activite.ressourceId, deltaMs, c, mien, compte, bonusAlma)
      if (maj) butinRecolte = true
      return maj ?? p
    }

    if (p.activite.type === "donjon") {
      const donjonId = p.activite.donjonId
      const maj = pasDescente(p, donjonId, dt, c, rng, inventaire, mien, bonusAlma, compte, etat.bossVaincus)
      if (Object.keys(mien).length > 0) butinRecolte = true
      if (!maj.fin) return maj.perso
      // **La série continue toute seule.** Tant qu'il reste des descentes
      // demandées, une clé pour entrer, et un personnage debout. Une défaite
      // arrête tout : enchaîner sur un personnage à terre brûlerait le reste des
      // clés pour rien (Vyshu, 26/08/2026).
      const suite = enchainer(maj.perso, donjonId, c, etat, inventaire, rng, maj.fin)
      // Le bilan ne remonte qu'à la fin de la série : sinon on aurait une
      // bannière par descente, exactement ce qu'on vient de retirer.
      if (suite.fin) fins.push(suite.fin)
      return suite.perso
    }

    const f = famille(c, p.activite.familleId)
    if (!f || f.monstres.length === 0) {
      // La famille a disparu du contenu (redécoupage, tranche réduite).
      // On libère le personnage plutôt que de le laisser tourner dans le vide :
      // l'interface l'affichera comme inactif et invitera à choisir une cible.
      return { ...p, activite: null, cible: null, tempsSurCible: 0 }
    }

    const s = statsPerso(p, c, compte)
    let { pv, cible, tempsSurCible, xp, niveau, kills, pointsLibres } = p
    let repasDans = attente

    if (cible === null || !monstre(c, cible)) {
      cible = choisirCible(f.monstres, rng)
      tempsSurCible = 0
    }

    let m = monstre(c, cible)!
    let pal = palierDe(m)

    // Les points de vie ne remontent jamais tout seuls : ce qu'on encaisse est
    // perdu jusqu'à ce qu'on mange (GDD §4). C'est ce qui donne un rôle à la
    // récolte et au four : sans vivres, un farm s'arrête tout seul.
    pv -= degatsSubisPs(s, pal) * dt

    // Manger avant de tomber, plutôt que tomber puis revenir cliquer. Le joueur
    // choisit ses vivres en les fabriquant ; le personnage les emploie au bon
    // moment tout seul.
    if (pv < s.pvMax * SEUIL_REPAS && pv > 0 && attente <= 0) {
      const repas = meilleurRepas(c, inventaire, s.pvMax - pv, s.pvMax)
      if (repas) {
        const n = inventaire[repas.id]! - 1
        if (n > 0) inventaire[repas.id] = n
        else delete inventaire[repas.id]
        pv = Math.min(s.pvMax, pv + soinBrut(repas) * s.soin)
        mange[repas.id] = (mange[repas.id] ?? 0) + 1
        repasDans = DELAI_REPAS_MS
      }
    }

    if (pv <= 0) {
      // Mort : le farm s'arrête. Pas de minuteur, pas de perte, mais il faudra
      // revenir cliquer pour relancer (GDD §4).
      return { ...p, pv: 0, arrete: true, cible: null, tempsSurCible: 0, repasDans }
    }

    tempsSurCible += deltaMs
    let n = 0
    let seuil = tempsParKill(s, pal) * 1000
    const nivChasse = niveauMetier(p, CHASSEUR)
    let xpChasse = 0

    while (tempsSurCible >= seuil && n < KILLS_MAX_PAR_PAS) {
      tempsSurCible -= seuil
      n++
      kills++
      killsFamille[f.id] = (killsFamille[f.id] ?? 0) + 1

      for (const b of m.butin) {
        const taux = (b.taux[pal.palier - 1] ?? b.taux[0] ?? 0) * (1 + s.prospection / 100)
        if (rng() * 100 < taux) {
          mien[b.item] = (mien[b.item] ?? 0) + 1
          butinRecolte = true

          // Le Chasseur se lève ici (GDD §22) : c'est le seul métier qui
          // progresse au combat, sur le gibier que les monstres lâchaient déjà
          // sans que personne n'en soit crédité.
          const pris = item(c, b.item)
          if (pris && gibiers(c).has(pris.id)) {
            xpChasse += xpParGibier(pris)
            // Le niveau de Chasseur ne change pas la cadence — elle appartient
            // au combat — mais le rendement : une chance de pièce en plus.
            if (rng() < bonusChasse(nivChasse)) mien[b.item] = (mien[b.item] ?? 0) + 1
          }
        }
      }

      xp += Math.round(xpGagnee(pal.niveau, niveau) * (1 + (s.sagesse + bonusAlma) / 100))

      // --- avis de recherche : se résout tout seul, aucune présence requise
      if (rng() < TAUX_RECHERCHE) {
        const id = tirer(c.recherches, recherches, pal.niveau, rng, f.zone)
        const cible = id !== null ? monstre(c, id) : null
        if (cible) {
          const pc = palierDe(cible)
          // Le recherché est nettement plus costaud : le vaincre demande de
          // tenir la durée du combat sans tomber.
          const duree = tempsParKill(s, pc) * 1.5
          const subis = dpsMonstre(pc.niveau) * 1.5 * duree
          const vaincu = subis < pv
          dernierRecherche = { monstre: cible.id, vaincu }
          if (vaincu) {
            if (!recherches.includes(cible.id)) recherches.push(cible.id)
            for (const b of cible.butin) {
              if (rng() * 100 < (b.taux[0] ?? 0) * 2) mien[b.item] = (mien[b.item] ?? 0) + 1
            }
          } else {
            pv = 0
          }
        }
      }

      // --- archimonstre : il faut être là pour le capturer
      if (!apparition && rng() < TAUX_ARCHIMONSTRE) {
        const id = tirer(c.archimonstres, etat.archimonstres, pal.niveau, rng, f.zone)
        if (id !== null) apparition = { monstre: id, expire: etat.lastTick + deltaMs + DUREE_ARCHIMONSTRE_MS }
      }
      while (xp >= xpRequise(niveau)) {
        xp -= xpRequise(niveau)
        niveau++
        pointsLibres += POINTS_PAR_NIVEAU
      }

      cible = choisirCible(f.monstres, rng)
      m = monstre(c, cible)!
      pal = palierDe(m)
      seuil = tempsParKill(s, pal) * 1000
    }

    return {
      ...p, pv, cible, tempsSurCible, xp, niveau, kills, pointsLibres, repasDans,
      metiers: xpChasse > 0 ? monterChasse(p, xpChasse) : p.metiers,
    }
  })

  for (const mien of parPerso.values()) {
    for (const [id, n] of Object.entries(mien)) butin[Number(id)] = (butin[Number(id)] ?? 0) + n
  }
  const range = rangerButin({ ...etat, inventaire }, butin, butinRecolte || Object.keys(mange).length > 0, rng, c)

  // Le butin du pas est recollé sur chaque personnage, pour le journal. On ne
  // réécrit l'objet que s'il change, afin de ne pas casser l'égalité de
  // référence sur laquelle s'appuie le rendu.
  const avecButin = personnages.map((p) => {
    const mien = parPerso.get(p.id) ?? {}
    const vide = Object.keys(mien).length === 0
    if (vide && Object.keys(p.dernierButin ?? {}).length === 0) return p
    return { ...p, dernierButin: mien }
  })

  const derniereFin = fins[fins.length - 1] ?? null
  const bossVaincus = derniereFin?.reussi
    ? [...new Set([...etat.bossVaincus, ...(donjon(c, derniereFin.donjon)?.boss ?? [])])]
    : etat.bossVaincus

  return {
    ...etat, personnages: avecButin, apparition, archiRates, recherches, dernierRecherche,
    derniereDescente: derniereFin ?? etat.derniereDescente, bossVaincus, killsFamille,
    ...range,
  }
}

/**
 * Un pas de descente pour un personnage.
 *
 * Le donjon se joue comme le farm : on encaisse, on mange si besoin, on abat le
 * monstre au contact. La différence est qu'on ne choisit pas sa cible et qu'on
 * ne peut pas s'arrêter — tomber, c'est perdre la descente et la clé.
 */
function pasDescente(
  p: Personnage, donjonId: number, dt: number, c: Contenu, rng: () => number,
  inventaire: Record<number, number>, mien: Record<number, number>,
  bonusAlma: number, compte: ReturnType<typeof effetsDofus>, etatBoss: number[],
): { perso: Personnage; fin: Descente | null } {
  const d = donjon(c, donjonId)
  if (!d || !p.descente) return { perso: { ...p, activite: null, descente: null }, fin: null }
  const s = statsPerso(p, c, compte)

  let pv = p.pv
  let repasDans = p.repasDans ?? 0
  let vivres = 0
  // Manger d'abord : entrer dans la salle suivante à trois points de vie et
  // mourir avant la première bouchée n'apprend rien au joueur. Le délai vaut
  // ici comme ailleurs — c'est lui qui fait du donjon une épreuve d'endurance.
  if (pv < s.pvMax * SEUIL_REPAS && pv > 0 && repasDans <= 0) {
    const repas = meilleurRepas(c, inventaire, s.pvMax - pv, s.pvMax)
    if (repas) {
      const n = inventaire[repas.id]! - 1
      if (n > 0) inventaire[repas.id] = n
      else delete inventaire[repas.id]
      pv = Math.min(s.pvMax, pv + soinBrut(repas) * s.soin)
      repasDans = DELAI_REPAS_MS
      vivres++
    }
  }

  const pas = pasDonjon(s, d, c, p.descente, dt, rng, s.sagesse + bonusAlma, p.niveau, pv)
  let { xp, niveau, kills, pointsLibres } = p
  xp += pas.xp
  kills += pas.kills
  while (xp >= xpRequise(niveau)) {
    xp -= xpRequise(niveau)
    niveau++
    pointsLibres += POINTS_PAR_NIVEAU
  }

  // Les vivres consommés se cumulent dans la descente, comme l'XP et la durée.
  const suivi = { ...pas.descente, vivres: pas.descente.vivres + vivres }

  if (!pas.fini) {
    // `cible` sert au journal, qui nomme le vaincu d'après elle : sans ça, une
    // descente entière ne consignait que des « Monstre vaincu ».
    return {
      perso: {
        ...p, pv: pas.pv, xp, niveau, kills, pointsLibres, repasDans,
        descente: suivi, cible: suivi.restants[0] ?? p.cible,
      },
      fin: null,
    }
  }

  // Fin de descente : le butin accumulé rejoint celui du pas, et le personnage
  // ressort de l'activité. Un échec ne rend rien — la clé a déjà été consommée.
  if (pas.fini === "gagne") {
    for (const [id, n] of Object.entries(suivi.butin)) mien[Number(id)] = (mien[Number(id)] ?? 0) + n
  }
  const fin: Descente = {
    donjon: d.id, reussi: pas.fini === "gagne", salle: suivi.salle,
    salles: d.salles, vaincus: suivi.vaincus,
    butin: pas.fini === "gagne" ? suivi.butin : {},
    xp: suivi.xp, dureeS: suivi.dureeS, vivres: suivi.vivres,
    // Un boss déjà couché ne « rejoint » plus rien : le dire évite d'annoncer
    // une nouveauté qui n'en est pas.
    premiere: pas.fini === "gagne" && !d.boss.every((b) => etatBoss.includes(b)),
    niveaux: niveau - p.niveau,
  }
  return {
    perso: {
      ...p, pv: Math.max(0, pas.pv), xp, niveau, kills, pointsLibres, repasDans,
      descente: null, activite: null, cible: null, tempsSurCible: 0,
      arrete: pas.fini === "perdu",
    },
    fin,
  }
}

/**
 * Sous ce seuil de vie, le personnage mange s'il a de quoi.
 *
 * Trop haut, il gaspille des vivres pour trois points de vie ; trop bas, il
 * meurt entre deux bouchées. La moitié laisse la place à un repas entier sans
 * gâcher.
 */
const SEUIL_REPAS = 0.5

/**
 * Le repas le plus juste : celui qui rend le plus sans dépasser franchement ce
 * qui manque. On ne mange pas un gâteau de niveau 200 pour douze points de vie.
 */
function meilleurRepas(c: Contenu, inv: Record<number, number>, manquant: number, pvMax: number) {
  let choix: Item | null = null
  for (const [id, n] of Object.entries(inv)) {
    if (n <= 0) continue
    const it = item(c, Number(id))
    if (!it || !estConsommable(it)) continue
    const soin = soinBrut(it)
    if (soin <= 0) continue
    // On préfère le plus gros qui ne dépasse pas ; à défaut, le plus petit
    // disponible, pour ne pas garder un blessé faute de miette.
    const gaspille = Math.max(0, soin - manquant)
    const dejaGaspille = choix ? Math.max(0, soinBrut(choix) - manquant) : Infinity
    const mieux = !choix || gaspille < dejaGaspille
      || (gaspille === dejaGaspille && soin > soinBrut(choix))
    if (mieux) choix = it
  }
  return choix
}

/**
 * Plafond du coffre : **une protection de la sauvegarde, rien d'autre.**
 *
 * Il valait d'abord 150, et c'était dix fois trop sévère. Mesuré en production :
 * une pièce pèse 105 octets en moyenne — son identifiant, son objet, et son jet
 * ligne par ligne —, la plus grosse sauvegarde du jeu fait 21 Ko, et le serveur
 * en accepte 512. Autrement dit, le coffre pouvait accueillir des milliers de
 * pièces sans gêner personne ; on en refusait cent cinquante-et-unième.
 *
 * Le vrai risque est à l'autre bout : sans aucun plafond, une partie laissée à
 * tourner des semaines finit par produire une sauvegarde que le serveur
 * **refuse**, et l'on ne sauvegarde plus du tout. Mille places de base, deux
 * mille au maximum : à 105 octets pièce, cela fait 205 Ko, soit moins de la
 * moitié de ce qui est permis, et un joueur n'y arrive pas en jouant
 * normalement.
 */
export const PLAFOND_COFFRE = 1_000

/**
 * Ce que l'équipe peut entreposer, pods compris.
 *
 * Les **pods** sont la capacité de transport chez Ankama, et le jeu n'a pas de
 * système de poids : ils ne servaient donc à rien, alors que des centaines
 * d'objets en donnent. Ils agrandissent le coffre, qui est la réserve commune
 * de l'équipe — d'où la somme sur tous les personnages, et non le seul actif :
 * on ne veut pas que la capacité change en changeant de personnage affiché.
 *
 * Cent pods valent une place. Le plafond reste borné : la sauvegarde entière
 * part sur le réseau à chaque synchronisation.
 */
/**
 * Ce que les pods ajoutent au coffre.
 *
 * On garde le lien — sinon la statistique « Pods » de l'équipement ne servirait
 * plus à rien, et le jeu en a déjà assez d'affichées pour rien (§4). Mais elle
 * n'est plus le verrou : elle ajoute mille places à un socle qui en compte déjà
 * mille.
 */
export const PODS_PAR_PLACE = 100
export const PLAFOND_COFFRE_MAX = 1_500

export function plafondCoffre(etat: Etat, c?: Contenu): number {
  const pods = etat.personnages.reduce((a, p) => a + statsPerso(p, c).pods, 0)
  return Math.min(PLAFOND_COFFRE_MAX, PLAFOND_COFFRE + Math.floor(pods / PODS_PAR_PLACE))
}

/**
 * Ce qu'une pièce vaut pour le joueur, pour décider laquelle sacrifier.
 * Le niveau seul garderait des jets ratés ; la perfection seule garderait une
 * coiffe de niveau 2 parfaite devant une coiffe de niveau 60 correcte.
 */
/**
 * Ce qu'une pièce vaut **pour le joueur**, quand il faut choisir laquelle des
 * arrivées ne rentrera pas.
 *
 * Le premier critère était `niveau × perfection`. Il paraissait raisonnable et
 * il était faux : un Chienchien de niveau 1 y pesait 120 contre 20 520 pour une
 * baguette de niveau 171, alors que les deux demandent à peu près deux heures de
 * farm. Un familier tombé dans un coffre plein repartait donc aussitôt en kamas.
 *
 * On classe donc par **rareté** — le temps qu'il faut pour l'obtenir (§20), qui
 * est la seule mesure honnête de ce qu'une pièce coûte —, la perfection ne
 * servant qu'à départager deux exemplaires du même objet.
 */
const interet = (c: Contenu, x: Porte): number => {
  const it = item(c, x.item)
  if (!it) return 0
  // **Une pièce travaillée à la forge ne se sacrifie jamais.** Elle affiche par
  // construction une perfection au plancher quand elle est maudite (§19), et
  // elle a pu coûter des dizaines de millions.
  if (forgee(it, x)) return Number.MAX_SAFE_INTEGER
  // Sans provenance connue, on retombe sur le niveau : mieux que rien, et ces
  // pièces-là ne tombent de toute façon pas d'un monstre.
  const rarete = tempsPourUn(c, it) ?? it.niveau * 60
  return rarete * (1 + perfection(it, x) / 100)
}

/** Cette pièce porte-t-elle la trace d'un passage à la forge ? */
function forgee(it: Item, x: Porte): boolean {
  return lignes(it, x).some((l) => l.surpuissance > 0 || l.malediction > 0)
}

/**
 * Répartit le butin du pas : les ressources s'empilent en quantités, les pièces
 * d'équipement deviennent des **exemplaires** avec leur propre jet.
 *
 * Sans cette distinction, une Coiffe droppée arrivait dans l'inventaire de
 * ressources sous forme de compteur : impossible à porter, et elle encombrait
 * l'onglet Ressources pendant que l'onglet Équipement restait vide.
 */
function rangerButin(
  etat: Etat, butin: Record<number, number>, actif: boolean,
  rng: () => number, c: Contenu,
  /**
   * Pièces déjà placées dans `etat.coffre` qui doivent pourtant compter comme
   * **des arrivées**. Une fabrication en série produit en vrac : son surplus
   * repart en kamas comme un butin, sans quoi elle contournerait le plafond.
   */
  arrivees: ReadonlySet<string> = new Set(),
): Pick<Etat, "inventaire" | "coffre" | "kamas"> {
  // Rattrapage des sauvegardes antérieures : les pièces déjà droppées dorment
  // dans l'inventaire, où elles ne servent à rien. On leur donne un jet.
  const aConvertir = Object.entries(etat.inventaire).filter(([id]) => {
    const it = item(c, Number(id))
    return it && slotDe(it)
  })

  // Le coffre ne contient que des pièces portables. Une clé de donjon, rangée là
  // par l'ancien écran de jet, y était invisible pour tous les écrans qui la
  // cherchent : elle redescend dans l'inventaire.
  const intrus = etat.coffre.filter((x) => {
    const it = item(c, x.item)
    return !it || !slotDe(it)
  })

  if (!actif && aConvertir.length === 0 && intrus.length === 0
      && etat.coffre.length <= plafondCoffre(etat, c)) {
    return { inventaire: etat.inventaire, coffre: etat.coffre, kamas: etat.kamas }
  }

  const inventaire = { ...etat.inventaire }
  for (const x of intrus) inventaire[x.item] = (inventaire[x.item] ?? 0) + 1
  const neuves: Porte[] = []
  const ajoute = (it: Item, n: number) => {
    for (let k = 0; k < n; k++) neuves.push(jeter(it, rng))
  }

  for (const [id, n] of aConvertir) {
    ajoute(item(c, Number(id))!, Math.min(n, plafondCoffre(etat, c)))
    delete inventaire[Number(id)]
  }
  if (actif) {
    for (const [id, n] of Object.entries(butin)) {
      const it = item(c, Number(id))
      if (it && slotDe(it)) ajoute(it, n)
      else inventaire[Number(id)] = (inventaire[Number(id)] ?? 0) + n
    }
  }

  // **Un coffre plein revend ce qui vient d'arriver, jamais ce qu'on possédait.**
  //
  // Le tri portait d'abord sur le coffre entier : la pièce la moins
  // « intéressante » partait, et un familier de niveau 1 l'est toujours moins
  // qu'une arme de niveau 200. Un joueur a perdu les siens sans rien voir passer.
  //
  // Ce qu'on garde volontairement est donc intouchable. Seul le butin du pas en
  // cours peut être revendu à l'arrivée, et l'on garde les meilleurs d'entre eux
  // — un bon drop entre toujours, c'est le surplus qui repart en kamas.
  const gardees = etat.coffre.filter((x) => !intrus.includes(x) && !arrivees.has(x.uid))
  const neuves2 = [...etat.coffre.filter((x) => arrivees.has(x.uid)), ...neuves]
  let kamas = etat.kamas
  const plafond = plafondCoffre(etat, c)
  // La place restante peut être nulle, voire négative si le plafond a baissé —
  // déséquiper une pièce à pods en fait autant. On ne jette rien pour autant :
  // le coffre reste au-dessus de sa limite, et plus rien n'y entre.
  const place = Math.max(0, plafond - gardees.length)

  let coffre = gardees
  if (neuves2.length <= place) {
    coffre = [...gardees, ...neuves2]
  } else {
    // **Un objet qu'on ne possède pas encore passe devant.** Le premier
    // Chienchien compte ; le cinquième beaucoup moins. Aucun classement par
    // valeur ne dira jamais ça, parce que ça ne dépend pas de l'objet mais de
    // ce qu'on a déjà.
    const deja = new Set<number>([
      ...gardees.map((x) => x.item),
      ...etat.personnages.flatMap((p) => Object.values(p.equipement).map((x) => x?.item ?? -1)),
    ])
    const parInteret = [...neuves2].sort((a, b) =>
      Number(deja.has(a.item)) - Number(deja.has(b.item)) || interet(c, b) - interet(c, a))
    coffre = [...gardees, ...parInteret.slice(0, place)]
    for (const x of parInteret.slice(place)) {
      const it = item(c, x.item)
      if (it) kamas += prixVente(it)
    }
  }

  return { inventaire, coffre, kamas }
}

/**
 * Épingle ou dépingle une recette. C'est une préférence d'affichage, mais elle
 * vit dans l'état parce qu'elle doit suivre le joueur d'un appareil à l'autre.
 */
export function epingler(etat: Etat, resultat: number): Etat {
  const dedans = etat.epingles.includes(resultat)
  return {
    ...etat,
    epingles: dedans ? etat.epingles.filter((x) => x !== resultat)
                     : [...etat.epingles, resultat],
  }
}

/** Capture l'archimonstre visible. Rien ne se passe s'il a déjà expiré. */
export function capturer(etat: Etat, maintenant: number): Etat {
  const a = etat.apparition
  if (!a || expiree(a, maintenant)) return { ...etat, apparition: null }
  return {
    ...etat,
    apparition: null,
    archimonstres: etat.archimonstres.includes(a.monstre)
      ? etat.archimonstres
      : [...etat.archimonstres, a.monstre],
  }
}

export const oublierRecherche = (etat: Etat): Etat => ({ ...etat, dernierRecherche: null })

/**
 * Dépose l'offrande du jour. Le rendez-vous quotidien du jeu : il oblige à
 * garder un fond de stock plutôt que tout vendre ou tout crafter (GDD §10).
 */
export function offrirAlmanax(etat: Etat, c: Contenu, maintenant: number): Etat {
  const jour = jourDe(maintenant)
  if (etat.almanaxJour === jour) return etat
  const o = offrandeDuJour(c, maintenant)
  if (!o || (etat.inventaire[o.item] ?? 0) < o.qte) return etat

  const inventaire = { ...etat.inventaire }
  const reste = inventaire[o.item]! - o.qte
  if (reste > 0) inventaire[o.item] = reste
  else delete inventaire[o.item]

  return { ...etat, inventaire, almanaxJour: jour, almanaxJusqua: finAlmanax(maintenant) }
}

export type { Apparition }

/**
 * Un pas d'auto-récolte. Pas de PV, pas de mort : le seul verrou est le niveau
 * de métier (GDD §5).
 */
function pasRecolte(
  p: Personnage, ressourceId: number, deltaMs: number,
  c: Contenu, butin: Record<number, number>,
  compte: ReturnType<typeof effetsDofus>,
  bonusAlma: number,
): Personnage | null {
  const r = recolte(c, ressourceId)
  if (!r) return { ...p, activite: null, tempsSurCible: 0 }
  if (!accessible(p, r)) return { ...p, activite: null, tempsSurCible: 0 }

  const niv = niveauMetier(p, r.metier)
  const seuil = (tempsRecolte(r, niv) / statsPerso(p, c, compte).recolte) * 1000
  let tempsSurCible = p.tempsSurCible + deltaMs
  let n = 0
  let { niveau, xp } = p.metiers[r.metier] ?? { niveau: 1, xp: 0 }

  while (tempsSurCible >= seuil && n < KILLS_MAX_PAR_PAS) {
    tempsSurCible -= seuil
    n++
    butin[r.item] = (butin[r.item] ?? 0) + 1
    // **L'Almanax vaut aussi pour les métiers.** Le bouton promet « +50 % d'XP »
    // sans réserve, et le bonus ne portait que sur le combat et les donjons : un
    // joueur qui récoltait ne voyait strictement rien pour son offrande. Vyshu
    // l'a signalé — « j'ai acheté le bonus d'exp +50 % via l'Almanax, mais je
    // vois pas de diff ». Une promesse tenue à moitié se lit comme un bug.
    //
    // La Sagesse, elle, reste au combat : c'est son sens chez Ankama, et
    // l'étendre changerait la valeur d'une caractéristique qu'on achète avec ses
    // points, pas d'une offrande qu'on renouvelle chaque jour.
    xp += Math.round(xpParRecolte(r) * (1 + bonusAlma / 100))
    while (niveau < NIVEAU_METIER_MAX && xp >= xpRequiseMetier(niveau)) {
      xp -= xpRequiseMetier(niveau)
      niveau++
    }
  }
  if (n === 0) return { ...p, tempsSurCible }

  return {
    ...p, tempsSurCible,
    metiers: { ...p.metiers, [r.metier]: { niveau, xp } },
  }
}

const choisirCible = (ids: number[], rng: () => number) => ids[Math.floor(rng() * ids.length)]!

// ---------------------------------------------------------------- craft

/**
 * Lance un craft : consomme les ingrédients et produit un jet, qui reste **en
 * attente**. On ne remplace jamais l'équipement porté sans montrer d'abord le
 * résultat : le moment « je garde ou je jette » est le plaisir du système
 * (GDD §6), il ne doit pas être automatisé.
 *
 * `doubleJet` correspond à l'Écaflip : deux tirages, on garde le meilleur.
 */
export function crafter(etat: Etat, r: Recette, c: Contenu): Etat {
  const it = item(c, r.resultat)
  if (!it || etat.enAttente || !craftable(r, etat.inventaire)) return etat

  // Une clé de donjon, un alliage, un pain : rien de tout cela ne se porte et
  // rien n'a de jet. Ces crafts vont droit dans l'inventaire. Sans ce test, le
  // jeu proposait « Équiper » pour une clé de donjon.
  if (!slotDe(it)) {
    const inventaire = consommer(etat.inventaire, r)
    inventaire[it.id] = (inventaire[it.id] ?? 0) + 1
    return { ...etat, inventaire }
  }

  // Le double jet vient de la classe du personnage actif : c'est l'Écaflip
  // branché sur le système de craft (GDD §3), pas un réglage d'interface.
  const actif = personnageActif(etat)
  const rng = alea(etat.seed)
  const porte = jeter(it, rng, Boolean(actif && effetDe(actif.classe).doubleJet) || effetsDofus(etat).doubleJet)
  const pct = perfection(it, porte)

  return {
    ...etat,
    seed: rng.graine,
    inventaire: consommer(etat.inventaire, r),
    enAttente: porte,
    // Le record retient le meilleur jet jamais obtenu, même refusé : c'est un
    // compteur de collection, pas un inventaire.
    recordsCraft: pct > (etat.recordsCraft[it.id] ?? -1)
      ? { ...etat.recordsCraft, [it.id]: pct }
      : etat.recordsCraft,
  }
}

/**
 * Combien de fois cette recette est réalisable, ingrédients en main.
 *
 * Sert au bouton « MAX » : lancer trois cents fabrications une par une n'a pas
 * de sens quand on a de quoi les faire.
 */
export function combienCraftable(r: Recette, inventaire: Record<number, number>): number {
  let n = Infinity
  for (const g of r.ingredients) {
    if (g.qte <= 0) continue
    n = Math.min(n, Math.floor((inventaire[g.item] ?? 0) / g.qte))
  }
  return Number.isFinite(n) ? Math.max(0, n) : 0
}

/** Plafond d'un lot : au-delà, un clic bloquerait l'onglet une seconde entière. */
export const LOT_MAX = 500

/**
 * Fabrique en série.
 *
 * Une fabrication à l'unité pose une question — garder ce jet, le ranger, le
 * jeter ? Un lot n'en pose aucune : **tout part au coffre**, ou dans
 * l'inventaire pour ce qui n'a pas de jet. C'est la différence entre soigner
 * une pièce et produire du pain.
 *
 * Le record de perfection est mis à jour comme à l'unité : c'est un compteur de
 * collection, il ne dépend pas de ce qu'on décide de garder.
 */
export function crafterLot(etat: Etat, r: Recette, c: Contenu, combien: number): Etat {
  const it = item(c, r.resultat)
  if (!it || etat.enAttente) return etat

  const n = Math.min(Math.max(0, Math.floor(combien)), LOT_MAX, combienCraftable(r, etat.inventaire))
  if (n <= 0) return etat

  let inventaire = { ...etat.inventaire }
  // Ce qui n'a pas d'emplacement n'a pas de jet : ça s'empile, sans détour.
  if (!slotDe(it)) {
    for (let i = 0; i < n; i++) inventaire = consommer(inventaire, r)
    inventaire[it.id] = (inventaire[it.id] ?? 0) + n
    return { ...etat, inventaire }
  }

  const actif = personnageActif(etat)
  const double = Boolean(actif && effetDe(actif.classe).doubleJet) || effetsDofus(etat).doubleJet
  const rng = alea(etat.seed)
  const coffre = [...etat.coffre]
  let record = etat.recordsCraft[it.id] ?? -1

  for (let i = 0; i < n; i++) {
    inventaire = consommer(inventaire, r)
    const porte = jeter(it, rng, double)
    coffre.push(porte)
    record = Math.max(record, perfection(it, porte))
  }

  // Le coffre se rabote comme après un butin : le surplus **de cette série**
  // est revendu plutôt que de gonfler la sauvegarde. Ce qu'on gardait déjà n'y
  // passe pas — c'est tout l'objet du plafond revu.
  const nouvelles = new Set(coffre.slice(etat.coffre.length).map((x) => x.uid))
  const range = rangerButin({ ...etat, inventaire, coffre }, {}, true, rng, c, nouvelles)
  return {
    ...etat, ...range, seed: rng.graine,
    recordsCraft: { ...etat.recordsCraft, [it.id]: record },
  }
}

/** L'emplacement où poser une pièce : pour un anneau, le libre d'abord, sinon
 *  le moins bon des deux. */
function slotCible(p: Personnage, it: Item, base: Slot): Slot {
  if (base !== "anneau1") return base
  const a = p.equipement.anneau1, b = p.equipement.anneau2
  if (!a) return "anneau1"
  if (!b) return "anneau2"
  return perfection(it, a) <= perfection(it, b) ? "anneau1" : "anneau2"
}

/**
 * Équipe une pièce sur le personnage actif. La pièce remplacée **retourne au
 * coffre** au lieu de disparaître : c'est un objet possédé, pas une case.
 */
function poser(etat: Etat, porte: Porte, c: Contenu): Etat {
  const it = item(c, porte.item)
  const base = it ? slotDe(it) : null
  const p = personnageActif(etat)
  if (!it || !base || !p) return etat

  // Le niveau requis vaut aussi pour un craft qu'on vient de sortir : refuser
  // ici est le seul endroit qui couvre tous les chemins vers un emplacement.
  if (!portable(it, p.niveau)) return etat

  const slot = slotCible(p, it, base)
  const remplacee = p.equipement[slot]
  const equipe: Partial<Record<Slot, Porte>> = { ...p.equipement, [slot]: porte }
  return {
    ...etat,
    coffre: [...etat.coffre.filter((x) => x.uid !== porte.uid), ...(remplacee ? [remplacee] : [])],
    personnages: etat.personnages.map((x) => (x.id === p.id ? { ...x, equipement: equipe } : x)),
  }
}

/** Équipe le jet en attente. */
export function garderJet(etat: Etat, c: Contenu): Etat {
  const porte = etat.enAttente
  if (!porte) return etat
  return { ...poser(etat, porte, c), enAttente: null }
}

/** Range le jet en attente dans le coffre sans l'équiper. */
export function rangerJet(etat: Etat): Etat {
  const porte = etat.enAttente
  if (!porte) return etat
  return { ...etat, enAttente: null, coffre: [...etat.coffre, porte] }
}

/** Abandonne le jet en attente. Les ingrédients restent consommés. */
export const jeterJet = (etat: Etat): Etat => ({ ...etat, enAttente: null })

/** Équipe une pièce prise dans le coffre. */
export function equiper(etat: Etat, uid: string, c: Contenu): Etat {
  const porte = etat.coffre.find((x) => x.uid === uid)
  return porte ? poser(etat, porte, c) : etat
}

/** Retire une pièce et la **renvoie au coffre**. */
export function deposer(etat: Etat, slot: Slot): Etat {
  const p = personnageActif(etat)
  const porte = p?.equipement[slot]
  if (!p || !porte) return etat
  const equipe = { ...p.equipement }
  delete equipe[slot]
  return {
    ...etat,
    coffre: [...etat.coffre, porte],
    personnages: etat.personnages.map((x) => (x.id === p.id ? { ...x, equipement: equipe } : x)),
  }
}

/** Détruit une pièce du coffre contre sa valeur de revente. */
export function vendreEquipement(etat: Etat, uid: string, c: Contenu): Etat {
  const porte = etat.coffre.find((x) => x.uid === uid)
  const it = porte ? item(c, porte.item) : null
  if (!porte || !it) return etat
  return {
    ...etat,
    coffre: etat.coffre.filter((x) => x.uid !== uid),
    kamas: etat.kamas + prixVente(it),
  }
}

/**
 * Vend **plusieurs** pièces du coffre d'un coup (GDD §21).
 *
 * Un joueur remonte de farm avec quarante pièces à liquider ; les vendre une par
 * une demandait quatre-vingts gestes. Le lot est la même opération, répétée dans
 * le moteur plutôt que dans le poignet.
 *
 * Les identifiants inconnus sont ignorés sans bruit : l'écran a pu être
 * reconstruit entre la sélection et le clic, et une pièce déjà partie ne doit
 * pas faire échouer les trente-neuf autres.
 */
export function vendreLot(etat: Etat, uids: string[], c: Contenu): { etat: Etat; gain: number; vendues: number } {
  const set = new Set(uids)
  let gain = 0
  let vendues = 0
  for (const x of etat.coffre) {
    if (!set.has(x.uid)) continue
    const it = item(c, x.item)
    if (!it) continue
    gain += prixVente(it)
    vendues++
  }
  if (vendues === 0) return { etat, gain: 0, vendues: 0 }
  return {
    etat: {
      ...etat,
      coffre: etat.coffre.filter((x) => !set.has(x.uid) || !item(c, x.item)),
      kamas: etat.kamas + gain,
    },
    gain, vendues,
  }
}

/** Broie plusieurs pièces d'un coup. Même raison, même précaution. */
export function broyerLot(etat: Etat, uids: string[], c: Contenu): { etat: Etat; broyees: number } {
  let e = etat
  let broyees = 0
  for (const uid of uids) {
    const avant = e
    e = broyer(e, uid, c)
    if (e !== avant) broyees++
  }
  return { etat: e, broyees }
}

/**
 * Vend plusieurs **lots de ressources** d'un coup.
 *
 * Chaque entrée dit combien vendre de quoi : la sélection multiple envoie tout
 * le stock, mais le champ de quantité permet de n'en vendre qu'une partie —
 * garder de quoi crafter tout en liquidant le surplus est précisément
 * l'arbitrage du §7.
 */
export function vendreRessources(
  etat: Etat, lots: { item: number; qte: number }[], c: Contenu,
): { etat: Etat; gain: number; lots: number } {
  let e = etat
  let gain = 0
  let n = 0
  for (const l of lots) {
    const avant = e.kamas
    e = vendre(e, l.item, l.qte, c)
    if (e.kamas !== avant) { gain += e.kamas - avant; n++ }
  }
  return { etat: e, gain, lots: n }
}

/**
 * Broie une pièce du coffre : elle disparaît, ses matériaux entrent en réserve.
 *
 * C'est la contrepartie de la vente. Vendre donne des kamas, broyer donne des
 * Pépites et des Tourmalines, seule source de ces deux matériaux et donc porte
 * d'entrée vers l'équipement de haut niveau (GDD §8).
 */
export function broyer(etat: Etat, uid: string, c: Contenu): Etat {
  const porte = etat.coffre.find((x) => x.uid === uid)
  const it = porte ? item(c, porte.item) : null
  if (!porte || !it) return etat
  const inventaire = { ...etat.inventaire }
  for (const [id, n] of Object.entries(broyage(it))) {
    inventaire[Number(id)] = (inventaire[Number(id)] ?? 0) + n
  }
  return { ...etat, coffre: etat.coffre.filter((x) => x.uid !== uid), inventaire }
}

// ---------------------------------------------------------------- marché

/** Une ligne du journal du marché, telle que le serveur la remet. */
export interface Credit {
  id: number
  kamas: number
  item: number | null
  quantite: number
  jet: Record<number, number> | null
  motif: string
}

/**
 * Applique ce que le serveur doit au joueur (GDD §20).
 *
 * La sauvegarde appartient au client : le serveur ne peut pas y écrire. Il tient
 * un journal, on le rejoue ici, et l'on retient le numéro où l'on s'est arrêté
 * dans `hdvCurseur`. Une ligne déjà appliquée est ignorée — c'est le curseur qui
 * rend l'opération sûre à répéter, et non un accusé de réception, qu'un onglet
 * fermé au mauvais moment aurait perdu.
 *
 * Les pièces d'équipement reviennent au **coffre** avec leur jet intact : c'est
 * tout l'objet d'un marché de pièces forgées.
 */
export function appliquerCredits(etat: Etat, credits: Credit[], c: Contenu): Etat {
  const neufs = credits.filter((x) => x.id > (etat.hdvCurseur ?? 0))
  if (neufs.length === 0) return etat

  let kamas = etat.kamas
  const inventaire = { ...etat.inventaire }
  const coffre = [...etat.coffre]

  for (const x of neufs) {
    kamas += Math.max(0, x.kamas ?? 0)
    if (!x.item || x.quantite <= 0) continue
    const it = item(c, x.item)
    if (!it) continue
    if (x.jet) {
      // Un exemplaire unique : on lui donne un identifiant local, celui du
      // vendeur n'ayant aucun sens dans ce compte-ci.
      coffre.push({ uid: `m${x.id}`, item: it.id, jet: x.jet })
    } else {
      inventaire[it.id] = (inventaire[it.id] ?? 0) + x.quantite
    }
  }

  return {
    ...etat, kamas, inventaire, coffre,
    hdvCurseur: Math.max(etat.hdvCurseur ?? 0, ...neufs.map((x) => x.id)),
  }
}

/**
 * Retire de la partie ce qu'on met en vente.
 *
 * Le dépôt se fait en deux temps — on sort la marchandise ici, le serveur la
 * prend là-bas — et l'ordre compte : on n'appelle ceci qu'**après** que le
 * serveur a accepté l'annonce. L'inverse ferait disparaître un lot dès que le
 * réseau tousse.
 */
export function sortirPourVente(
  etat: Etat, item_: number, quantite: number, uid: string | null,
): Etat {
  if (uid) {
    const dedans = etat.coffre.some((x) => x.uid === uid)
    if (!dedans) return etat
    return { ...etat, coffre: etat.coffre.filter((x) => x.uid !== uid) }
  }
  const reste = (etat.inventaire[item_] ?? 0) - quantite
  const inventaire = { ...etat.inventaire }
  if (reste > 0) inventaire[item_] = reste
  else delete inventaire[item_]
  return { ...etat, inventaire }
}

// ---------------------------------------------------------------- forgemagie

/** Où vit un exemplaire : au coffre, ou porté par quelqu'un. */
export function trouverPorte(
  etat: Etat, uid: string,
): { porte: Porte; ou: "coffre" } | { porte: Porte; ou: "equipe"; perso: string; slot: Slot } | null {
  const auCoffre = etat.coffre.find((x) => x.uid === uid)
  if (auCoffre) return { porte: auCoffre, ou: "coffre" }
  for (const p of etat.personnages) {
    for (const [slot, x] of Object.entries(p.equipement)) {
      if (x?.uid === uid) return { porte: x, ou: "equipe", perso: p.id, slot: slot as Slot }
    }
  }
  return null
}

/** Repose un exemplaire modifié là où il était. */
function reposerPorte(etat: Etat, uid: string, porte: Porte): Etat {
  const ou = trouverPorte(etat, uid)
  if (!ou) return etat
  if (ou.ou === "coffre") {
    return { ...etat, coffre: etat.coffre.map((x) => (x.uid === uid ? porte : x)) }
  }
  return {
    ...etat,
    personnages: etat.personnages.map((p) => (p.id === ou.perso
      ? { ...p, equipement: { ...p.equipement, [ou.slot]: porte } }
      : p)),
  }
}

export interface Forgeage {
  etat: Etat
  issues: Issue[]
  /** Ce que le lot a coûté en tout. */
  depense: number
}

/**
 * Tente `combien` fois de pousser une ligne (GDD §19).
 *
 * Le lot n'est pas un confort : perfectionner une pièce de niveau 200 demande
 * cent soixante-dix tentatives, et les faire une par une serait cent soixante-
 * dix clics. On s'arrête dès que la ligne est au maximum ou que les kamas
 * manquent — payer dans le vide n'apprendrait rien au joueur.
 *
 * La pièce peut être **portée** : c'est même le cas normal, on forge ce qu'on
 * utilise. Elle est reposée là où on l'a prise.
 */
export function forgemagie(
  etat: Etat, uid: string, effet: number, combien: number, c: Contenu,
): Forgeage {
  const ou = trouverPorte(etat, uid)
  const it = ou ? item(c, ou.porte.item) : null
  if (!ou || !it) return { etat, issues: [{ type: "impossible", raison: "introuvable" }], depense: 0 }

  const rng = alea(etat.seed ^ hachage(uid) ^ effet)
  let porte = ou.porte
  let kamas = etat.kamas
  const issues: Issue[] = []

  // **Un lot ne franchit pas le maximum tout seul.**
  //
  // Parti sous le maximum, il s'y arrête. Sans ce butoir, « jusqu'en haut »
  // enchaînait sur la surpuissance, dont chaque cran coûte deux fois et demie
  // le précédent : un seul clic pouvait vider des dizaines de millions sans
  // qu'on l'ait demandé. Parti d'une ligne déjà au maximum, en revanche, c'est
  // bien la surpuissance qu'on vient chercher.
  const depart = lignes(it, porte).find((x) => x.effet === effet)
  const versLeMax = depart ? !auMaximum(depart) : false

  for (let k = 0; k < Math.max(1, Math.min(combien, LOT_FORGE_MAX)); k++) {
    const t = forger(it, porte, effet, kamas, rng)
    issues.push(t.issue)
    porte = t.porte
    kamas = t.kamas
    if (t.issue.type === "impossible") break
    const l = lignes(it, porte).find((x) => x.effet === effet)
    if (versLeMax && l && auMaximum(l)) break
  }

  return {
    etat: {
      ...reposerPorte(etat, uid, porte),
      kamas,
      // La graine avance : deux lots identiques ne rejouent pas la même chance.
      seed: (etat.seed * 1_664_525 + 1_013_904_223) >>> 0,
      forges: (etat.forges ?? 0) + issues.filter((i) => i.type === "reussite").length,
      // Les crans arrachés **au-dessus** du maximum se comptent à part : c'est
      // l'exploit, et il vaut son propre palier de succès.
      surpuissances: (etat.surpuissances ?? 0)
        + issues.filter((i) => i.type === "reussite" && i.surpuissance).length,
    },
    issues,
    depense: etat.kamas - kamas,
  }
}

/** Un entier stable tiré d'un identifiant, pour semer le tirage. */
const hachage = (s: string): number => {
  let h = 2_166_136_261
  for (let k = 0; k < s.length; k++) h = Math.imul(h ^ s.charCodeAt(k), 16_777_619)
  return h >>> 0
}

/**
 * Verse l'expérience de chasse et fait passer les niveaux qu'elle débloque.
 *
 * Même escalier que les autres métiers : c'est ce qui permet de les comparer
 * entre eux, et surtout de ne pas avoir deux règles à retenir.
 */
function monterChasse(p: Personnage, gagne: number): Personnage["metiers"] {
  let { niveau, xp } = p.metiers[CHASSEUR] ?? { niveau: 1, xp: 0 }
  xp += gagne
  while (niveau < NIVEAU_METIER_MAX && xp >= xpRequiseMetier(niveau)) {
    xp -= xpRequiseMetier(niveau)
    niveau++
  }
  return { ...p.metiers, [CHASSEUR]: { niveau, xp } }
}

export const LOT_FORGE_MAX = 200

/**
 * Maudit une ligne : elle descend d'un cran, la pièce gagne de la marge.
 *
 * Séparé de `forgemagie` à dessein : ce n'est pas une tentative, c'est une
 * décision. Pas de lot non plus — on ne sacrifie pas dix crans d'un clic.
 */
export function maudireLigne(etat: Etat, uid: string, effet: number, c: Contenu): Forgeage {
  const ou = trouverPorte(etat, uid)
  const it = ou ? item(c, ou.porte.item) : null
  if (!ou || !it) return { etat, issues: [{ type: "impossible", raison: "introuvable" }], depense: 0 }

  const t = maudire(it, ou.porte, effet, etat.kamas)
  if (t.issue.type === "impossible") return { etat, issues: [t.issue], depense: 0 }

  return {
    etat: { ...reposerPorte(etat, uid, t.porte), kamas: t.kamas },
    issues: [t.issue],
    depense: etat.kamas - t.kamas,
  }
}

/**
 * Dépense `combien` points dans une caractéristique, sans jamais dépasser la
 * réserve. `Infinity` met tout ce qui reste.
 *
 * Le point par point était intenable : au niveau 100, cinq cents points à
 * placer font cinq cents clics.
 */
export function repartir(
  etat: Etat, carac: keyof Personnage["carac"], combien: number,
): Etat {
  const p = personnageActif(etat)
  if (!p || p.pointsLibres <= 0) return etat
  const n = Math.min(p.pointsLibres, Math.max(1, Math.floor(combien)))
  return {
    ...etat,
    personnages: etat.personnages.map((x) => (x.id === p.id
      ? { ...x, pointsLibres: x.pointsLibres - n, carac: { ...x.carac, [carac]: x.carac[carac] + n } }
      : x)),
  }
}

/**
 * Rend tous les points répartis, pour recommencer.
 *
 * Un joueur qui découvre le jeu place ses premiers points au hasard, et se
 * retrouve enfermé dans un choix qu'il ne comprenait pas encore. Gratuit et
 * sans limite : la punition n'apporterait rien ici, il n'y a pas d'adversaire
 * humain à protéger d'une optimisation.
 */
export function remiseAZeroCarac(etat: Etat): Etat {
  const p = personnageActif(etat)
  if (!p) return etat
  const total = Object.values(p.carac).reduce((a, b) => a + b, 0)
  if (total === 0) return etat
  const vide = { vitalite: 0, force: 0, intelligence: 0, chance: 0, agilite: 0, sagesse: 0 }
  return {
    ...etat,
    personnages: etat.personnages.map((x) => (x.id === p.id
      ? { ...x, carac: vide, pointsLibres: x.pointsLibres + total,
          // Les PV maximum changent avec la Vitalité : on ramène la vie dans
          // les clous plutôt que de laisser un personnage à 3 000 / 50.
          pv: Math.min(x.pv, 50 + x.niveau * 10) }
      : x)),
  }
}

/**
 * Mange un vivre, à la demande du joueur.
 *
 * Le personnage mange déjà tout seul sous la moitié de sa vie ; ceci sert à
 * reprendre l'avantage avant un passage difficile, ou à vider un stock qu'on ne
 * veut plus porter. Manger à pleine vie ne fait rien : le vivre serait perdu.
 */
export function manger(etat: Etat, itemId: number, c: Contenu): Etat {
  const p = personnageActif(etat)
  const it = item(c, itemId)
  if (!p || !it || !estConsommable(it)) return etat
  if ((etat.inventaire[itemId] ?? 0) <= 0) return etat
  const s = statsPerso(p, c, effetsDofus(etat))
  if (p.pv >= s.pvMax) return etat
  // Le délai vaut aussi pour le geste manuel : sinon il suffisait de cliquer
  // pour contourner la limite que le personnage s'impose tout seul.
  if ((p.repasDans ?? 0) > 0) return etat

  const inventaire = { ...etat.inventaire }
  const reste = inventaire[itemId]! - 1
  if (reste > 0) inventaire[itemId] = reste
  else delete inventaire[itemId]

  return {
    ...etat,
    inventaire,
    personnages: etat.personnages.map((x) => (x.id === p.id
      ? { ...x, pv: Math.min(s.pvMax, x.pv + soinBrut(it) * s.soin), repasDans: DELAI_REPAS_MS }
      : x)),
  }
}

// ---------------------------------------------------------------- personnages

/** Le personnage ciblé par les actions (équiper, craft, donjon). */
export function personnageActif(etat: Etat): Personnage | undefined {
  return etat.personnages.find((p) => p.id === etat.persoActif) ?? etat.personnages[0]
}

export function choisirPersonnage(etat: Etat, id: string): Etat {
  return etat.personnages.some((p) => p.id === id) ? { ...etat, persoActif: id } : etat
}

/**
 * Achète un slot de personnage (GDD §9).
 *
 * C'est le puits à kamas principal, et surtout : chaque classe branche le
 * compte sur un **système différent**. Le deuxième personnage n'est pas un
 * robinet de plus, c'est de la vitesse (Xélor), de la chance au craft
 * (Écaflip) ou du butin (Enutrof).
 */
export function acheterPersonnage(etat: Etat, classeId: number, nom: string, id: string): Etat {
  const cout = coutSlot(etat.personnages.length)
  if (cout === null || etat.kamas < cout) return etat

  const neuf: Personnage = {
    id, nom, classe: classeId, niveau: 1, xp: 0,
    carac: { vitalite: 0, force: 0, intelligence: 0, chance: 0, agilite: 0, sagesse: 0 },
    metiers: {}, equipement: {}, pv: 0, activite: null, arrete: false,
    cible: null, tempsSurCible: 0, pointsLibres: 0, kills: 0,
  }
  return {
    ...etat,
    kamas: etat.kamas - cout,
    personnages: [...etat.personnages, { ...neuf, pv: statsPerso(neuf, undefined).pvMax }],
    persoActif: etat.persoActif ?? id,
  }
}

export { coutSlot, SLOTS_MAX }

// ---------------------------------------------------------------- zones

/**
 * Le monde est **entièrement ouvert**, dès la première minute.
 *
 * Il y a eu deux tentatives de verrou avant celle-ci, un péage en kamas puis
 * une condition de niveau, et les deux étaient superflues : le jeu dit déjà,
 * pour chaque famille, si le personnage y tient ou en combien de temps il
 * tombera. C'est le seul verrou nécessaire, et c'est celui de Dofus : on peut
 * marcher partout, ce sont les monstres qui décident.
 *
 * Le puits à kamas est le recrutement de personnages (§9) : désirable, jamais
 * bloquant.
 */
export const zoneOuverte = (): boolean => true

// ---------------------------------------------------------------- donjons

export interface ResultatDescente {
  etat: Etat
  descente: Descente | null
  /** raison d'un refus, quand la descente n'a pas pu être lancée */
  refus?: "personnage" | "cle" | "inconnu"
}

/**
 * Lance une descente. La clé est consommée à l'entrée et **perdue même en cas
 * d'échec** : c'est elle qui limite les tentatives, pas un minuteur arbitraire
 * (GDD §8).
 */
/** Descentes maximum dans une série. */
export const SERIE_MAX = 50

export function lancerDonjon(
  etat: Etat, donjonId: number, c: Contenu, series = 1,
): ResultatDescente {
  const d = donjon(c, donjonId)
  const p = personnageActif(etat)
  if (!d) return { etat, descente: null, refus: "inconnu" }
  if (!p) return { etat, descente: null, refus: "personnage" }
  if (!possedeCles(d, etat.inventaire)) return { etat, descente: null, refus: "cle" }

  const rng = alea(etat.seed)
  const en = entrer(d, c, rng)
  if (!en) return { etat, descente: null, refus: "inconnu" }

  const inventaire = { ...etat.inventaire }
  for (const k of d.cles) {
    const reste = (inventaire[k.item] ?? 0) - k.qte
    if (reste > 0) inventaire[k.item] = reste
    else delete inventaire[k.item]
  }

  // On entre à pleine vie : la clé paie l'entrée, et un donjon commencé blessé
  // ne se jouerait pas, il se lirait. Ce qu'on y perd, en revanche, on le
  // ressort avec.
  const s = statsPerso(p, c, effetsDofus(etat))
  const personnages = etat.personnages.map((x) => x.id !== p.id ? x : {
    ...x, activite: { type: "donjon" as const, donjonId },
    descente: en, pv: s.pvMax, arrete: false, cible: en.restants[0] ?? null, tempsSurCible: 0,
    // Une série ne peut pas dépasser ce que les clés permettent, ni ce plafond :
    // on ne lance pas mille descentes d'un geste qu'on ne pourrait plus arrêter.
    enchaine: Math.max(1, Math.min(Math.floor(series), SERIE_MAX)),
    descenteCumul: null,
  })

  return {
    etat: { ...etat, seed: rng.graine, inventaire, personnages, derniereDescente: null },
    descente: null,
  }
}


/**
 * Relance la descente suivante d'une série, ou clôt le bilan.
 *
 * Le bilan cumule ce que la série a produit — butin, XP, durée, vivres,
 * victoires — parce que c'est ce qu'on veut lire à la fin : « à la fin on aurait
 * l'inventaire de tous les items drops ». `reussi` dit alors si la série est
 * allée à son terme, et `gagnees` combien de descentes ont été gagnées.
 */
function enchainer(
  p: Personnage, donjonId: number, c: Contenu, etat: Etat,
  inventaire: Record<number, number>, rng: () => number,
  fin: Descente,
): { perso: Personnage; fin: Descente | null } {
  const fait = fin.reussi ? 1 : 0
  const cumul: Descente = {
    ...fin,
    runs: (p.descenteCumul?.runs ?? 0) + 1,
    gagnees: (p.descenteCumul?.gagnees ?? 0) + fait,
    vaincus: (p.descenteCumul?.vaincus ?? 0) + fin.vaincus,
    xp: (p.descenteCumul?.xp ?? 0) + fin.xp,
    dureeS: (p.descenteCumul?.dureeS ?? 0) + fin.dureeS,
    vivres: (p.descenteCumul?.vivres ?? 0) + fin.vivres,
    niveaux: (p.descenteCumul?.niveaux ?? 0) + fin.niveaux,
    premiere: fin.premiere || Boolean(p.descenteCumul?.premiere),
    butin: fusionner(p.descenteCumul?.butin ?? {}, fin.butin),
  }

  const restants = (p.enchaine ?? 0) - 1
  const d = donjon(c, donjonId)
  if (!fin.reussi || restants <= 0 || !d || !possedeCles(d, inventaire)) {
    return { perso: { ...p, enchaine: 0, descenteCumul: null }, fin: cumul }
  }

  const en = entrer(d, c, rng)
  if (!en) return { perso: { ...p, enchaine: 0, descenteCumul: null }, fin: cumul }
  for (const k of d.cles) {
    const reste = (inventaire[k.item] ?? 0) - k.qte
    if (reste > 0) inventaire[k.item] = reste
    else delete inventaire[k.item]
  }
  const s = statsPerso(p, c, effetsDofus(etat))
  return {
    perso: {
      ...p, enchaine: restants, descenteCumul: cumul,
      activite: { type: "donjon", donjonId },
      descente: en, pv: s.pvMax, arrete: false,
      cible: en.restants[0] ?? null, tempsSurCible: 0,
    },
    fin: null,
  }
}

/** Somme de deux tables de butin. */
const fusionner = (a: Record<number, number>, b: Record<number, number>) => {
  const t = { ...a }
  for (const [k, n] of Object.entries(b)) t[Number(k)] = (t[Number(k)] ?? 0) + n
  return t
}

// ---------------------------------------------------------------- économie

/**
 * Revend un lot au PNJ. Seule source de kamas du jeu (GDD §7), et donc
 * arbitrage permanent, puisque vendre et crafter puisent dans le même stock.
 */
export function vendre(etat: Etat, itemId: number, qte: number, c: Contenu): Etat {
  const it = item(c, itemId)
  const stock = etat.inventaire[itemId] ?? 0
  const n = Math.min(qte, stock)
  if (!it || n <= 0) return etat

  const inventaire = { ...etat.inventaire }
  if (stock - n > 0) inventaire[itemId] = stock - n
  else delete inventaire[itemId]

  return { ...etat, inventaire, kamas: etat.kamas + prixVente(it) * n }
}

/** Relance un personnage arrêté par la mort. Ses PV repartent au maximum. */
export function relancer(p: Personnage): Personnage {
  return { ...p, arrete: false, pv: statsPerso(p).pvMax, cible: null, tempsSurCible: 0 }
}

export type { Monstre }
